package at.theatervogelweide.admin.web.components

import androidx.compose.runtime.Composable
import at.theatervogelweide.domain.TheaterTime
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime

/**
 * Dates and times are typed the Austrian way and parsed leniently with [TheaterTime]: `7.11.26`, `07.11.` and
 * `2026-11-07` all become `07.11.2026` when the field loses focus. Validate with [dateError] and [timeError].
 */
@Composable
fun AdminDateField(
    id: String,
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
    error: String? = null,
    hint: String? = null,
    optional: Boolean = false,
    /** As in [AdminTextField]. */
    required: Boolean = !optional,
) {
    AdminTextField(
        id = id,
        label = label,
        value = text,
        onValueChange = onTextChange,
        error = error,
        hint = hint,
        optional = optional,
        required = required,
        placeholder = "TT.MM.JJJJ",
        inputMode = "numeric",
        autocomplete = "off",
        onBlur = { TheaterTime.parseDate(text)?.let { onTextChange(TheaterTime.format(it)) } },
    )
}

/** Like [AdminDateField] for a time of day: `19.30`, `19h30` and `19 Uhr` become `19:30`. */
@Composable
fun AdminTimeField(
    id: String,
    label: String,
    text: String,
    onTextChange: (String) -> Unit,
    error: String? = null,
    hint: String? = null,
    optional: Boolean = false,
) {
    AdminTextField(
        id = id,
        label = label,
        value = text,
        onValueChange = onTextChange,
        error = error,
        hint = hint,
        optional = optional,
        placeholder = "HH:MM",
        inputMode = "numeric",
        autocomplete = "off",
        onBlur = { TheaterTime.parseTime(text)?.let { onTextChange(TheaterTime.format(it)) } },
    )
}

/** The date in [text], `null` when it is blank or can't be read. */
fun parseDateField(text: String): LocalDate? = TheaterTime.parseDate(text)

/** The time in [text], `null` when it is blank or can't be read. */
fun parseTimeField(text: String): LocalTime? = TheaterTime.parseTime(text)

/** The message for an [AdminDateField], or `null` if [text] is a date, or blank and [optional]. */
fun dateError(text: String, optional: Boolean): String? = when {
    text.isBlank() -> if (optional) null else "Bitte ein Datum eingeben."
    parseDateField(text) == null -> "Bitte ein Datum im Format TT.MM.JJJJ eingeben."
    else -> null
}

/** The message for an [AdminTimeField], or `null` if [text] is a time, or blank and [optional]. */
fun timeError(text: String, optional: Boolean): String? = when {
    text.isBlank() -> if (optional) null else "Bitte eine Uhrzeit eingeben."
    parseTimeField(text) == null -> "Bitte eine Uhrzeit im Format HH:MM eingeben."
    else -> null
}
